import { type RefCallback, useCallback, useEffect, useRef, useState } from "react";

type Box = "border-box" | "content-box" | "device-pixel-content-box";

interface UseMeasuredHeightOptions {
  box?: Box;
}

/**
 * Measures an element's height via ResizeObserver and returns a callback ref to attach to it.
 *
 * Unlike a static `useRef` + `useResizeObserver`, the observer is (re)attached from the callback
 * ref, so it fires on *every* mount/unmount of the node. This matters for elements that mount
 * *after* their owner — e.g. content gated behind async data: a ref-object observer set up once on
 * the owner's first commit sees `null` and never re-runs, so it never measures the element. The
 * callback-ref form measures it whenever it appears.
 *
 * Returns `0` until the node is first measured, and again once it unmounts.
 */
function useMeasuredHeight<T extends HTMLElement = HTMLDivElement>(
  options: UseMeasuredHeightOptions = {}
): [RefCallback<T>, number] {
  const { box = "border-box" } = options;
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback<RefCallback<T>>(
    node => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      // A detached node takes no space — drop the last measurement, or consumers offsetting around an
      // element that has unmounted (e.g. a bar hidden on a state change) keep clearing a ghost.
      if (!node) {
        setHeight(0);
        return;
      }

      if (typeof ResizeObserver === "undefined") {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        if (!entry) {
          return;
        }
        const next = extractBlockSize(entry, box);
        setHeight(prev => (prev === next ? prev : next));
      });
      observer.observe(node, { box });
      observerRef.current = observer;
    },
    [box]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, height];
}

function boxSizeProp(box: Box): "borderBoxSize" | "contentBoxSize" | "devicePixelContentBoxSize" {
  switch (box) {
    case "border-box":
      return "borderBoxSize";
    case "device-pixel-content-box":
      return "devicePixelContentBoxSize";
    case "content-box":
      return "contentBoxSize";
  }
}

function extractBlockSize(entry: ResizeObserverEntry, box: Box): number {
  // `contentRect.height` is the content-box fallback for older engines that don't populate the
  // *BoxSize arrays.
  return entry[boxSizeProp(box)]?.[0]?.blockSize ?? entry.contentRect.height;
}

export default useMeasuredHeight;
